import {
	CATEGORY_ACCESS,
	OVERVIEW_LEVEL,
	TICKET_ACCESS_LEVEL,
} from "../constant/projectPermissions";

// Ticket levels (ALL/ASSIGNED_AND_OWN/OWN) plus the overview view-levels are all
// "granted" for the generic can(area, action) check - every member has at least
// basic overview, so can("overview","view") must be true for both overview levels.
const TRUTHY_LEVELS = new Set([
	TICKET_ACCESS_LEVEL.ALL,
	TICKET_ACCESS_LEVEL.ASSIGNED_AND_OWN,
	TICKET_ACCESS_LEVEL.OWN,
	OVERVIEW_LEVEL.OVERVIEW,
	OVERVIEW_LEVEL.OVERVIEW_FINANCIALS,
]);

export const isPermissionGranted = (value) => {
	if (value === true) return true;
	return TRUTHY_LEVELS.has(value);
};

export const resolveTicketCategoryIds = (member, isProjectAdmin) => {
	if (isProjectAdmin) return null;
	const access = member?.permissions?.ticketCategories?.categoryAccess;
	if (access === CATEGORY_ACCESS.ALL || access === undefined || access === null)
		return null;
	if (access === CATEGORY_ACCESS.NONE) return [];
	if (Array.isArray(access)) return access;
	return null;
};

export const filterByTicketCategoryScope = (
	items,
	getCategoryId,
	ticketCategoryIds,
) => {
	if (!Array.isArray(items)) return [];
	if (ticketCategoryIds === null) return items;
	if (ticketCategoryIds.length === 0) return [];
	const allowed = new Set(ticketCategoryIds);
	return items.filter((item) => {
		const categoryId = getCategoryId(item);
		return categoryId && allowed.has(categoryId);
	});
};

export const getTicketCreatorUser = (ticket) =>
	ticket?.creator ?? ticket?.ticketCreator ?? null;

export const getTicketAssigneeUser = (ticket) =>
	ticket?.assignee ?? ticket?.ticketAssignee ?? null;

export const getTicketOwnerId = (ticket) =>
	ticket?.createdBy ??
	ticket?.created_by ??
	getTicketCreatorUser(ticket)?.id ??
	null;

export const getTicketAssigneeId = (ticket) =>
	ticket?.assignedTo ??
	ticket?.assigned_to ??
	getTicketAssigneeUser(ticket)?.id ??
	null;

/**
 * Who may approve a ticket for invoicing (assignee-approves model):
 *  - a project admin may always approve;
 *  - otherwise the caller must hold the `tickets.approve` permission AND be the
 *    ticket's assignee - the assignee signs off their own work.
 * Separation of duties is preserved structurally: assignee ≠ creator is enforced
 * at create/reassign, so the approver can never be the ticket's creator.
 * Mirrors the hard check enforced on the backend.
 */
export const canApproveTicketRow = (
	ticket,
	{ isProjectAdmin, canApprove, userId },
) => {
	if (!ticket) return false;
	if (isProjectAdmin) return true;
	if (!canApprove) return false;
	return !!userId && getTicketAssigneeId(ticket) === userId;
};

export const formatTicketUserName = (user, { unknownLabel, unassignedLabel } = {}) => {
	if (!user) {
		return unassignedLabel ?? unknownLabel ?? "";
	}
	const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
	if (name) return name;
	if (user.email) return user.email;
	return unknownLabel ?? unassignedLabel ?? "";
};

export const filterTicketsByViewLevel = (tickets, viewLevel, userId) => {
	if (!Array.isArray(tickets)) return [];
	if (viewLevel === TICKET_ACCESS_LEVEL.NONE) return [];
	if (!userId || viewLevel === TICKET_ACCESS_LEVEL.ALL) return tickets;

	return tickets.filter((ticket) => {
		const createdBy = getTicketOwnerId(ticket);
		const assignedTo = getTicketAssigneeId(ticket);
		if (viewLevel === TICKET_ACCESS_LEVEL.OWN) return createdBy === userId;
		if (viewLevel === TICKET_ACCESS_LEVEL.ASSIGNED_AND_OWN) {
			return createdBy === userId || assignedTo === userId;
		}
		return true;
	});
};

export const canEditTicketRow = (ticket, ticketEditLevel, userId, canEdit) => {
	if (!canEdit) return false;
	if (ticketEditLevel === TICKET_ACCESS_LEVEL.ALL) return true;
	if (ticketEditLevel === TICKET_ACCESS_LEVEL.OWN) {
		return getTicketOwnerId(ticket) === userId;
	}
	return false;
};
